import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, ExternalLink, Github, Check, BookOpen, UserCheck, ShieldCheck, BarChart2, Server, Globe, LayoutDashboard, Users, Award, Lock, ListChecks, Rocket, User, Calendar, Layers, Wrench, TrendingUp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Helmet } from 'react-helmet-async';
import { projects, projectSeo } from '@/data/projects';

/** Projects that have a page here; the portfolio only links "Details" for these. */
export const detailProjectIds = new Set(projects.map(p => p.id));

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const project = projects.find(p => p.id === id);
  const isMobile = useIsMobile();

  // Custom JSX for Learn Kogit

  if (!project) {
    return (
      <div className="container-custom min-h-screen pt-20 pb-10 md:pt-24 md:pb-16 px-4">
        <div className="text-center">
          <h2 className="mb-4">Project Not Found</h2>
          <p className="mb-6 md:mb-8">Sorry, the project you're looking for doesn't exist.</p>
          <Button asChild>
            <Link to="/portfolio">Back to Portfolio</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <Helmet>
        <title>{projectSeo(project).title}</title>
        <meta name="description" content={projectSeo(project).description} />
        <link rel="canonical" href={`https://www.omnicraftservices.com/portfolio/${project.id}`} />
      </Helmet>
      {/* Hero Section */}
      <section className="py-8 md:py-16 bg-gradient-to-b from-primary/10 to-transparent dark:from-primary/5">
        <div className="container-custom">
          <Link to="/portfolio" className="inline-flex items-center text-primary mb-4 hover:underline text-sm md:text-base">
            <ArrowLeft size={16} className="mr-2" /> Back to Portfolio
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            <div>
              <h1 className="mb-3 md:mb-4">{project.title}</h1>
              <p className="text-base md:text-lg lg:text-xl text-slate-700 dark:text-slate-300 mb-4 md:mb-6">
                {project.fullDescription || project.description}
              </p>
              <div className="flex flex-wrap gap-3 md:gap-4 mb-6">
                {project.liveUrl && (
                  <Button asChild size={isMobile ? "default" : "lg"}>
                    <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={16} className="mr-2" /> Visit Website
                    </a>
                  </Button>
                )}
                {project.githubUrl && (
                  <Button asChild variant="outline" size={isMobile ? "default" : "lg"}>
                    <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                      <Github size={16} className="mr-2" /> GitHub Repo
                    </a>
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {project.client && (
                  <div>
                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Client</p>
                    <p className="font-medium text-sm md:text-base">{project.client}</p>
                  </div>
                )}
                {project.duration && (
                  <div>
                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Duration</p>
                    <p className="font-medium text-sm md:text-base">{project.duration}</p>
                  </div>
                )}
                {project.year && (
                  <div>
                    <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Year</p>
                    <p className="font-medium text-sm md:text-base">{project.year}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Category</p>
                  <p className="font-medium text-sm md:text-base">{project.category}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src={project.image} 
                alt={project.title} 
                className="w-full h-48 md:h-64 object-cover" 
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Technologies & Features */}
      <section className="py-8 md:py-16 bg-slate-50 dark:bg-slate-900/50">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="mb-4 md:mb-6">Technologies Used</h2>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {project.technologies.map((tech, index) => (
                  <span 
                    key={index} 
                    className="bg-white dark:bg-slate-800 px-3 py-1.5 md:px-4 md:py-2 rounded-md shadow-sm font-medium text-sm"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
            {project.features && project.features.length > 0 && (
              <div>
                <h2 className="mb-4 md:mb-6">Key Features</h2>
                <ul className="space-y-2 md:space-y-3">
                  {project.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-4 md:h-5 w-4 md:w-5 text-primary mr-2 md:mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-sm md:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Project Details */}
      {(project.challenges || project.solutions) && (
        <section className="py-8 md:py-16">
          <div className="container-custom">
            <h2 className="mb-6 md:mb-8">Project Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {project.challenges && project.challenges.length > 0 && (
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Challenges</h3>
                    <ul className="space-y-2">
                      {project.challenges.map((challenge, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-primary mr-2 flex-shrink-0">•</span>
                          <span className="text-sm md:text-base">{challenge}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              {project.solutions && project.solutions.length > 0 && (
                <Card>
                  <CardContent className="pt-6">
                    <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Solutions</h3>
                    <ul className="space-y-2">
                      {project.solutions.map((solution, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-primary mr-2 flex-shrink-0">•</span>
                          <span className="text-sm md:text-base">{solution}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Screenshots Gallery */}
      {project.screenshots && project.screenshots.length > 1 && (
        <section className="py-8 md:py-16 bg-slate-50 dark:bg-slate-900/50">
          <div className="container-custom">
            <h2 className="mb-6 md:mb-8">Project Gallery</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {project.screenshots.map((screenshot, index) => (
                <div key={index} className="rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={screenshot} 
                    alt={`${project.title} screenshot ${index + 1}`}
                    className="w-full h-48 md:h-64 object-cover hover:scale-105 transition-transform duration-300" 
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-10 md:py-16">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="mb-4 md:mb-6">Interested in Similar Results?</h2>
            <p className="text-base md:text-lg mb-6 md:mb-8 text-slate-600 dark:text-slate-400">
              Let's discuss how we can help you achieve your business goals with our comprehensive services.
              Contact us today to start your project.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
              <Button asChild size={isMobile ? "default" : "lg"}>
                <Link to="/contact">Start Your Project</Link>
              </Button>
              <Button asChild variant="outline" size={isMobile ? "default" : "lg"}>
                <Link to="/portfolio">View More Projects</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
